/**
 * List of all components that is used in
 * <component/> tag or dynamic components.
 *
 * The reason for this dictionary is because,
 * `is` in <component /> only accepts static variables
 * example <component is="test" />.
 * It doesn't allow variables with dynamic functionality for some reason.
 *
 * This is to prevent using resolveComponent which
 * requires the to make all components global.
 */

const atomsMap = {
  AtomsCta: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/Cta/index.vue')
    })
  },

  AtomsButton: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/Button/index.vue')
    })
  },

  AtomsButtonBack: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/Button/Back/index.vue')
    })
  },

  AtomsImage: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/Image/index.vue')
    })
  },

  AtomsLink: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/Link/index.vue')
    })
  },

  AtomsInput: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/Input/index.vue')
    })
  },

  AtomsHeadline: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/Headline/index.vue')
    })
  },

  AtomsDatepicker: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/Datepicker/index.vue')
    })
  },

  AtomsAuthorBlock: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/atoms/AuthorBlock/index.vue')
    })
  }
}

const moleculesMap = {
  MoleculesSelectDropdown: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/SelectDropdown/index.vue')
    })
  },

  MoleculesSelection: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Selection/index.vue')
    })
  },

  MoleculesStates: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/States/index.vue')
    })
  },

  MoleculesOccupation: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Occupation/index.vue')
    })
  },

  MoleculesKeyPoints: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/KeyPoints/index.vue')
    })
  },

  MoleculesInlineAge: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Inline/Age/index.vue')
    })
  },

  MoleculesInlineCardCta: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/v2/molecules/Inline/CardCta/index.vue')
    })
  },

  MoleculesInlineLifeStage: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Inline/LifeStage/index.vue')
    })
  },

  MoleculesInlinePartner: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Inline/Partner/index.vue')
    })
  },

  MoleculesVerticalFormWidget: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/v2/molecules/VerticalFormWidget/Sidebar/index.vue')
    })
  },

  MoleculesCalculatorWithDisclaimer: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Calculator/WithDisclaimer/index.vue')
    })
  },

  MoleculesPostcode: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Postcode/index.vue')
    })
  },

  MoleculesBirthdate: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Birthdate/index.vue')
    })
  },

  MoleculesCountry: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/molecules/Country/index.vue')
    })
  }
}

const organismMap = {}

const iconsMap = {
  IconsVerticalsHomeHealth: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/home/Health.vue')
    })
  },

  IconsVerticalsEnergy: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/Energy.vue')
    })
  },

  IconsVerticalsHomeEnergy: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/home/Energy.vue')
    })
  },

  IconsVerticalsLife: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/Life.vue')
    })
  },

  IconsVerticalsHomeLife: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/home/Life.vue')
    })
  },

  IconsVerticalsHomeLoan: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/HomeLoan.vue')
    })
  },

  IconsVerticalsHomeHouseLoans: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/home/HouseLoans.vue')
    })
  },

  IconsVerticalsCreditCard: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/CreditCard.vue')
    })
  },

  IconsVerticalsHomeCreditCard: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/home/CreditCard.vue')
    })
  },

  IconsVerticalsCarInsurance: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/CarInsurance.vue')
    })
  },

  IconsVerticalsTravelInsurance: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/TravelInsurance.vue')
    })
  },

  IconsVerticalsHomeCarInsurance: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/home/CarInsurance.vue')
    })
  },

  IconsVerticalsCarLoan: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/CarLoan.vue')
    })
  },

  IconsVerticalsHomeCarLoan: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/home/CarLoan.vue')
    })
  },

  IconsVerticalsPersonalLoan: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/PersonalLoan.vue')
    })
  },

  IconsVerticalsHomePersonalLoan: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/home/PersonalLoan.vue')
    })
  },

  IconsError: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/Error.vue')
    })
  },

  IconsClose: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/Close.vue')
    })
  },

  IconsRestrict: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/Restrict.vue')
    })
  },

  IconsGoldenStar: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/GoldenStar.vue')
    })
  },

  IconsTrash: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/Trash.vue')
    })
  },

  IconsFilter: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/Filter.vue')
    })
  },

  IconsVerticalsOVHC: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/OVHC.vue')
    })
  },

  IconsToolTip: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/ToolTip.vue')
    })
  },

  IconsCheckUnderline: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/CheckUnderline.vue')
    })
  },

  IconsVerticalsReverseMortgage: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/icons/verticals/ReverseMortgage.vue')
    })
  }
}

const templatesMap = {
  TemplatesContentfulContent: () => {
    return defineAsyncComponent({
      loader: () => import('~/components/templates/Contentful/Content/index.vue')
    })
  }
}

export const getComponent = component => {
  if (!component) {
    // eslint-disable-next-line no-console
    console.log(
      `%c${component}%c is undefined/null!`,
      'color: cyan; font-weight: bold;',
      'color: cyan;  font-weight: normal;'
    )
    return
  }

  try {
    const temp = getKey(component, {
      default: null,
      ...atomsMap,
      ...moleculesMap,
      ...organismMap,
      ...templatesMap,
      ...iconsMap
    })
    return temp?.()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)

    const temp = typeof component === 'object'
      ? JSON.stringify(component)
      : component

    // eslint-disable-next-line no-console
    console.log(
      `%c${temp}%c is not defined in dictionary!`,
      'color: cyan; font-weight: bold;',
      'color: cyan;  font-weight: normal;'
    )
  }
}

export const renderComponent = (key, map) => getKey(key, map)

